import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LessonCard = ({ lesson, isCompleted, isLocked, progress = 0, courseSlug, moduleSlug }) => {
  const navigate = useNavigate();

  const handlePlayLesson = () => {
    if (!isLocked) {
      if (courseSlug && moduleSlug && lesson?.id) {
        navigate(`/course/${courseSlug}/module/${moduleSlug}/lesson/${lesson.id}`);
      } else {
        // Fallback seguro: não navegar para rotas antigas
        console.warn('Missing slugs for navigation', { courseSlug, moduleSlug, lessonId: lesson?.id });
      }
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor((parseInt(seconds || 0, 10)) / 60);
    const remainingSeconds = (parseInt(seconds || 0, 10)) % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden transition-micro hover:shadow-tier-1 ${
      isLocked ? 'opacity-60' : 'hover:border-primary/20'
    }`}>
      <div className="flex flex-col sm:flex-row">
        {/* Thumbnail */}
        <div className="relative w-full sm:w-48 aspect-video sm:aspect-square">
          <Image
            src={lesson?.thumbnail}
            alt={lesson?.title}
            className="w-full h-full object-cover"
          />
          
          {/* Play Button Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            {isLocked ? (
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                <Icon name="Lock" size={20} className="text-muted-foreground" />
              </div>
            ) : (
              <button
                onClick={handlePlayLesson}
                className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-white transition-micro"
              >
                <Icon name="Play" size={20} className="text-primary ml-1" />
              </button>
            )}
          </div>

          {/* Progress Bar */}
          {progress > 0 && !isLocked && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black bg-opacity-20">
              <div
                className="h-full bg-secondary transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}

          {/* Status Badge */}
          <div className="absolute top-2 right-2">
            {isCompleted && (
              <div className="bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium">
                Concluído
              </div>
            )}
            {isLocked && (
              <div className="bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs font-medium">
                Bloqueado
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-card-foreground mb-1 line-clamp-2">
                {isLocked ? `Aula ${lesson?.order || 1}...` : lesson?.title}
              </h3>
              {!isLocked && (
                <p className="text-sm text-muted-foreground font-caption mb-3 line-clamp-2">
                  {lesson?.description}
                </p>
              )}
            </div>
          </div>

          {/* Lesson Meta */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={14} />
                <span className="font-caption">{formatDuration(lesson?.duration)}</span>
              </div>
            </div>

            {/* Action Button */}
            <Button
              variant={isLocked ? "ghost" : "outline"}
              size="sm"
              onClick={handlePlayLesson}
              disabled={isLocked}
              iconName={isLocked ? "Lock" : "Play"}
              iconPosition="left"
              iconSize={14}
            >
              {isLocked ? "Bloqueado" : "Assistir"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonCard;
